import { useMsal } from '@azure/msal-react'
import {
  HandCoins,
  Hourglass,
  Languages,
  ListTodo,
  LogOut,
  MoreVertical,
  ReceiptEuro,
  Share2,
  Store,
  Tag,
  Ticket,
  User,
  Wallet
} from 'lucide-react'
import { useCookies } from 'react-cookie'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router'
import { Permission, PermissionValue } from '../../api/usePermissions'
import Logo from '../../assets/images/logo-mark.svg?react'
import { envConfig } from '../../config'
import { usePermissionsContext } from '../../contexts/PermissionsContext'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../ui/dropdown-menu'
import {
  Sidebar as SidebarRoot,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar
} from '../ui/sidebar'

interface NavItem {
  label: string
  icon: typeof Store
  link: string
  requiredPermission?: PermissionValue
  external?: boolean
}

interface NavGroup {
  title: string
  items: NavItem[]
}

export function Sidebar() {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const config = envConfig()
  const [, setCookie] = useCookies(['lang'])
  const { instance, accounts } = useMsal()
  const { hasPermission } = usePermissionsContext()
  const { isMobile, setOpenMobile } = useSidebar()

  const user = accounts[0]
  const userName = user?.name || 'User'
  const userEmail = user?.username || 'user@example.com'

  const handleLogout = () => {
    instance.logoutRedirect()
  }

  const handleLanguageChange = () => {
    const newLang = i18n.language === 'de' ? 'en' : 'de'
    i18n.changeLanguage(newLang)
    setCookie('lang', newLang, { path: '/' })
  }

  const handleNavigate = () => {
    if (isMobile) {
      setOpenMobile(false)
    }
  }

  const navGroups: NavGroup[] = [
    {
      title: t('navigation.payments', { defaultValue: 'Zahlungen' }),
      items: [
        {
          label: t('navigation.paymentsOverview', {
            defaultValue: 'Zahlungsübersicht'
          }),
          icon: Wallet,
          link: '/admin/payments'
        },
        {
          label: t('navigation.requestPayment', {
            defaultValue: 'Zahlungen anfordern'
          }),
          icon: HandCoins,
          link: '/admin/request-adyen-payment'
        }
      ]
    },
    {
      title: t('navigation.administration', { defaultValue: 'Verwaltung' }),
      items: [
        {
          label: t('navigation.subMerchants', {
            defaultValue: 'Sub Merchants'
          }),
          icon: Store,
          link: '/admin/sub-merchants',
          requiredPermission: Permission.SUBMERCHANTS_READ
        },
        {
          label: t('navigation.monthlyBilling', {
            defaultValue: 'Monatsabrechnungen'
          }),
          icon: ReceiptEuro,
          link: '/admin/invoicing',
          requiredPermission: Permission.MONTHLY_BILLING_READ
        },
        {
          label: t('navigation.pendingTransfers', {
            defaultValue: 'Pending Transfers'
          }),
          icon: Hourglass,
          link: '/admin/pending-transfers',
          requiredPermission: Permission.MONTHLY_BILLING_READ
        },
        {
          label: t('navigation.shareRules', { defaultValue: 'Share Rules' }),
          icon: Share2,
          link: '/admin/share-rules',
          requiredPermission: Permission.SHARE_RULES_READ
        },
        {
          label: t('navigation.costCoverageCodes', {
            defaultValue: 'Cost Coverage Codes'
          }),
          icon: Tag,
          link: '/admin/cost-coverage-codes',
          requiredPermission: Permission.VOUCHER_CODES_READ
        },
        {
          label: t('navigation.costCoverageReservations', {
            defaultValue: 'Cost Coverage Reservations'
          }),
          icon: Ticket,
          link: '/admin/cost-coverage-reservations',
          requiredPermission: Permission.VOUCHER_CODES_READ
        },
        // Queue Dashboard is an external Bull Board page on the API host, so it
        // stays last in the group and renders as a plain anchor.
        {
          label: t('navigation.queueDashboard', {
            defaultValue: 'Queue Dashboard'
          }),
          icon: ListTodo,
          link: `${config.apiUrl}/queue`,
          requiredPermission: Permission.SHARE_RULES_READ,
          external: true
        }
      ]
    }
  ]

  const visibleGroups = navGroups
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) =>
          !item.requiredPermission || hasPermission(item.requiredPermission)
      )
    }))
    .filter((group) => group.items.length > 0)

  const isActive = (link: string) => location.pathname.startsWith(link)

  return (
    <SidebarRoot collapsible="icon" data-testid="app-sidebar">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/admin/payments" onClick={handleNavigate}>
                {/* size-8! beats the button's [&_svg]:size-4, which also hits nested svgs */}
                <div className="h-8 w-8 shrink-0 rounded-lg overflow-hidden">
                  <Logo className="size-8!" />
                </div>
                <div className="flex flex-col leading-none">
                  <span className="font-semibold text-sm">Bloomwell</span>
                  <span className="text-xs mt-0.5">Payment Service</span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent data-testid="app-sidebar-nav">
        {visibleGroups.map((group) => (
          <SidebarGroup key={group.title}>
            <SidebarGroupLabel className="text-[11px] font-semibold uppercase tracking-[0.08em] text-sidebar-foreground/70">
              {group.title}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => {
                  const Icon = item.icon

                  if (item.external) {
                    return (
                      <SidebarMenuItem key={item.label}>
                        <SidebarMenuButton asChild tooltip={item.label}>
                          <a
                            href={item.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={handleNavigate}
                          >
                            <Icon />
                            <span>{item.label}</span>
                          </a>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )
                  }

                  const active = isActive(item.link)

                  return (
                    <SidebarMenuItem key={item.label}>
                      <SidebarMenuButton
                        asChild
                        isActive={active}
                        tooltip={item.label}
                      >
                        <Link
                          to={item.link}
                          onClick={handleNavigate}
                          aria-current={active ? 'page' : undefined}
                        >
                          <Icon />
                          <span>{item.label}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter data-testid="app-sidebar-footer">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent"
                  data-testid="app-sidebar-account-menu-button"
                >
                  <div className="bg-gray-900 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
                    <User className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex flex-1 flex-col overflow-hidden text-left leading-none">
                    <span className="truncate font-semibold text-sm">
                      {userName}
                    </span>
                    <span className="mt-0.5 truncate text-xs text-muted-foreground">
                      {userEmail}
                    </span>
                  </div>
                  <MoreVertical className="ml-auto h-4 w-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" side="top" className="w-56">
                <DropdownMenuItem
                  onClick={handleLanguageChange}
                  className="cursor-pointer"
                >
                  <Languages className="mr-2 h-4 w-4" />
                  {i18n.language === 'de' ? 'English' : 'Deutsch'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-destructive focus:text-destructive cursor-pointer focus:bg-destructive/10"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('auth.logout', { defaultValue: 'Logout' })}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </SidebarRoot>
  )
}
